import type { ExamBundle } from './bundle';
import type { Store } from './port';
import type { ExamItemRow, ExamPartRow, ExamQuestionRow, ExamRow } from './rows';

/**
 * Die Inhalte einer Prüfung in die Tabellen schreiben — in **einer** Transaktion: erst die
 * alten Zeilen dieser Prüfung weg, dann die neuen hinein.
 *
 * ⚠️ Angefasst wird ausschließlich der **Inhalt**. `ExamLog` und `CandidateExamMetadata`
 * hängen am Prüfling, nicht an der Aufgabe, und bleiben stehen — ein Nachladen der Inhalte
 * darf erfasste Bewertungen nicht mitreißen. Ab Phase 4 kommt das Bündel vom Server statt
 * aus den Beispieldaten.
 */

/**
 * Löscht die Inhaltszeilen einer Prüfung — ohne eigene Transaktion. Personen bleiben — sie
 * hängen an mehreren Prüfungen. Nur für den Aufruf aus einer bereits laufenden Transaktion
 * heraus (siehe `importExam`); von außen ruft man stattdessen `clearExamContent`.
 */
async function löscheInhalt(store: Store, examId: string): Promise<void> {
  const parts = await store.all<ExamPartRow>('ExamPart', { examID: examId });
  for (const part of parts) {
    const container = await store.all<{ id: string }>('ExamItemContainer', { examPartID: part.id });
    for (const c of container) {
      // Medien hängen über `referenceType`/`referenceIdentifier` am Container, nicht über einen
      // Fremdschlüssel. Sie müssen deshalb hier von Hand weg — sonst bliebe ein Bild stehen,
      // das der Server aus der Station entfernt hat.
      await store.remove('Media', { referenceType: 'EXAM_ITEM_CONTAINER', referenceIdentifier: c.id });
      const items = await store.all<ExamItemRow>('ExamItem', { examItemContainerID: c.id });
      for (const item of items) {
        const fragen = await store.all<ExamQuestionRow>('ExamQuestion', { examItemID: item.id });
        for (const frage of fragen) {
          await store.remove('ExamAnswer', { examQuestionID: frage.id });
          await store.remove('ExamQuestion', { id: frage.id });
        }
        await store.remove('ExamItemPredefinedCommentPhraseList', { examItemID: item.id });
        await store.remove('ExamItem', { id: item.id });
      }
      await store.remove('ExamItemContainer', { id: c.id });
    }
    await store.remove('ExamPart', { id: part.id });
  }
  await store.remove('ExamExaminer', { examID: examId });
  await store.remove('ExamActor', { examID: examId });
  await store.remove('ExamCandidate', { examID: examId });
}

/**
 * Löscht die Inhaltszeilen einer Prüfung — für sich allein aufgerufen, in ihrer **eigenen**
 * Transaktion: bricht die Löschkaskade mittendrin ab, bleibt nichts halb gelöscht stehen.
 * Aus `importExam` heraus läuft stattdessen die transaktionslose `löscheInhalt` innerhalb von
 * dessen laufender Transaktion — `store.tx()` lässt sich nicht verschachteln.
 */
export async function clearExamContent(store: Store, examId: string): Promise<void> {
  await store.tx((t) => löscheInhalt(t, examId));
}

export async function importExam(store: Store, bundle: ExamBundle): Promise<void> {
  await store.tx(async (t) => {
    await löscheInhalt(t, bundle.exam.id);

    // Reihenfolge nach den Fremdschlüsseln: erst das Ziel, dann der Verweis.
    await t.put('Exam', [bundle.exam]);
    await t.put('ExamPart', bundle.parts);
    await t.put('ExamItemContainer', bundle.containers);
    await t.put('ExamItem', bundle.items);
    await t.put('ExamQuestion', bundle.questions);
    await t.put('ExamAnswer', bundle.answers);
    await t.put('Examiner', bundle.examiners);
    await t.put('ExamExaminer', bundle.examExaminers);
    await t.put('Actor', bundle.actors);
    await t.put('ExamActor', bundle.examActors);
    await t.put('Candidate', bundle.candidates);
    await t.put('ExamCandidate', bundle.examCandidates);

    if (bundle.media) await t.put('Media', bundle.media);

    // ⚠️ Bausteine werden **nur ersetzt, nicht vorher gelöscht.** Eine
    // `PredefinedCommentPhraseList` trägt kein `examID`; welche Liste zu dieser Prüfung gehört,
    // steht nirgends in der Tabelle selbst. Ein blindes Löschen träfe womöglich die Liste einer
    // anderen Prüfung. Weil die Kennungen beim Classic-Download aus den Serverkennungen
    // abgeleitet sind, trifft ein zweiter Download dieselben Zeilen und überschreibt sie.
    // Was der Server **entfernt** hat, bleibt dagegen stehen. Bekommt Relay eine Zuordnung
    // Liste↔Prüfung, gehört hier ein Löschen davor.
    if (bundle.phraseLists) await t.put('PredefinedCommentPhraseList', bundle.phraseLists);
    if (bundle.phrases) await t.put('PredefinedCommentPhrase', bundle.phrases);
    if (bundle.itemPhraseLists) {
      await t.put('ExamItemPredefinedCommentPhraseList', bundle.itemPhraseLists);
    }
    if (bundle.containerPhraseLists) {
      await t.put('ExamItemContainerPredefinedCommentPhraseList', bundle.containerPhraseLists);
    }

    // Nur im Classic-Modus gefüllt. Die Kennungen sind aus den Serverkennungen abgeleitet, ein
    // zweiter Download trifft also dieselben Zeilen — wie bei den Bausteinen oben.
    if (bundle.classicKennungen) await t.put('ClassicKennung', bundle.classicKennungen);
  });
}

/**
 * Was einer Löschung im Weg steht: die Bewertungen, die zu dieser Prüfung stehen.
 *
 * `gesamt` sind alle Protokollzeilen, `nichtÜbertragen` die davon, die der Server noch nie
 * gesehen hat (`transferred === 'N'`, s. `examLog.ts::pendingLogs`). Nur die zweite Zahl
 * beschreibt einen unwiederbringlichen Verlust — die erste steht auch schon auf dem Server.
 */
export async function examLoeschHindernis(
  store: Store,
  examId: string,
): Promise<{ gesamt: number; nichtÜbertragen: number; prüflinge: number }> {
  const zeilen = await store.all<{ candidateID: string | null; transferred: 'Y' | 'N' }>(
    'ExamLog',
    { examID: examId },
  );
  const prüflinge = new Set(zeilen.map((z) => z.candidateID).filter(Boolean));
  return {
    gesamt: zeilen.length,
    nichtÜbertragen: zeilen.filter((z) => z.transferred !== 'Y').length,
    prüflinge: prüflinge.size,
  };
}

/**
 * Löscht eine Prüfung samt allem, was an ihr hängt.
 *
 * ⚠️ **Die Reihenfolge ist keine Geschmacksfrage.** `ExamLog.examID` verweist mit
 * `ON DELETE RESTRICT` auf `Exam` (s. `schema.ts`): solange auch nur eine Protokollzeile
 * steht, weist SQLite das Löschen der `Exam`-Zeile ab. Also erst die Protokollzeilen, dann
 * die Zustandszeilen der Prüflinge, dann die Inhalte, zuletzt die Prüfung selbst.
 *
 * ⚠️ **Im Browser fällt ein Verstoss dagegen nicht auf.** Der IndexedDB-Adapter prüft keine
 * Fremdschlüssel (`web.ts`) — eine falsche Reihenfolge liefe dort durch und scheiterte erst
 * auf dem Gerät. Der Test dazu stellt die Reihenfolge deshalb ausdrücklich fest, statt sich
 * auf ein Scheitern zu verlassen, das im Testlauf nie einträte.
 *
 * Der Aufrufer entscheidet, **ob** gelöscht wird; `examLoeschHindernis` liefert ihm die Zahlen
 * für die Rückfrage.
 */
export async function deleteExam(store: Store, examId: string): Promise<void> {
  await store.tx(async (t) => {
    await t.remove('ExamLog', { examID: examId });
    await t.remove('CandidateExamMetadata', { examID: examId });
    // Ausdrücklich, nicht der Kaskade überlassen: der Browser-Adapter kennt keine
    // Fremdschlüssel, und beide Wege sollen dasselbe hinterlassen.
    await t.remove('ClassicKennung', { examID: examId });
    await löscheInhalt(t, examId);
    await t.remove('Exam', { id: examId });
  });
}

/** Ob die Inhalte dieser Prüfung auf dem Gerät liegen — das Feld `Exam.loaded` der Oberfläche. */
export async function isExamLoaded(store: Store, examId: string): Promise<boolean> {
  const treffer = await store.all<ExamRow>('Exam', { id: examId });
  return treffer.length > 0;
}

/**
 * Setzt das Prüfungspasswort einer abgelegten Prüfung (Ablageform, `domain/geraetePasswort.ts`).
 * Für Classic nach dem Herunterladen — die Abbildung selbst kennt nur den Klartext der Liste nicht.
 *
 * ⚠️ Die ganze Zeile wird neu geschrieben: die Web-Ablage ersetzt einen Satz, statt ihn zu mischen.
 */
export async function setzeGeraetePasswort(store: Store, examId: string, ablage: string | null): Promise<void> {
  const [zeile] = await store.all<ExamRow>('Exam', { id: examId });
  if (!zeile) return;
  await store.put('Exam', [{ ...zeile, devicePassword: ablage }]);
}

/** Das Prüfungspasswort einer abgelegten Prüfung, oder `null`. */
export async function leseGeraetePasswort(store: Store, examId: string): Promise<string | null> {
  const [zeile] = await store.all<ExamRow>('Exam', { id: examId });
  return zeile?.devicePassword ?? null;
}
